import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { fetchGames, type GameListItem } from "@/features/games/games.api";
import { addTrialGame, removeTrialGame } from "@/features/entitlements/entitlements.api";
import { useEntitlementsStore } from "@/features/entitlements/entitlements.store";
import { useNotification } from "@/app/context/NotificationContext";
import { ApiRequestError } from "@/shared/api/client";

const formatClock = (clock: string) => clock.replace("+", " | ");
const formatDate = (date: string) =>
  new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(date));

export const NikolaTrialSelector = () => {
  const { showNotification } = useNotification();
  const trial = useEntitlementsStore((state) => state.snapshot?.trial);
  const refresh = useEntitlementsStore((state) => state.refresh);
  const [games, setGames] = useState<GameListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    void fetchGames()
      .then((value) => setGames(value.filter((game) => game.rules === "chess" && game.exactTimeControl)))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  const selected = useMemo(() => new Map(trial?.selectedGames.map((game) => [game.gameId, game]) ?? []), [trial]);
  const candidates = useMemo(
    () =>
      games.filter(
        (game) => !trial?.exactTimeControl || game.exactTimeControl === trial.exactTimeControl || selected.has(game.id),
      ),
    [games, selected, trial?.exactTimeControl],
  );
  const choose = async (gameId: string) => {
    setSavingId(gameId);
    try {
      if (selected.has(gameId)) await removeTrialGame(gameId);
      else await addTrialGame(gameId);
      await refresh();
    } catch (error) {
      showNotification(
        error instanceof ApiRequestError ? error.message : "Nikola's Trial could not be updated. Try again.",
        "error",
      );
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Box sx={{ borderTop: "1px solid", borderColor: "divider", p: { xs: 2, sm: 2.5 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
        <Box>
          <Typography sx={{ fontWeight: 900 }}>Choose Nikola&apos;s Trial games</Typography>
          <Typography sx={{ mt: 0.4, color: "text.secondary", fontSize: ".75rem" }}>
            Choose one at a time. First choice sets exact clock; a game locks only after saved analysis.
          </Typography>
        </Box>
        <Chip
          label={`${(trial?.selectedGames.length ?? 0) + (trial?.deletedGameCount ?? 0)} / ${trial?.gameLimit ?? 10}${trial?.exactTimeControl ? ` · ${formatClock(trial.exactTimeControl)}` : ""}`}
        />
      </Stack>
      {(trial?.deletedGameCount ?? 0) > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {trial?.deletedGameCount} used slots belong to deleted games. Deleting analysis does not restore an allowance.
        </Typography>
      )}
      {loading && <CircularProgress size={20} sx={{ mt: 2 }} />}
      {loadError && (
        <Box role="alert" sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: ".75rem", fontWeight: 800 }}>Games could not be loaded.</Typography>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </Box>
      )}
      <Stack spacing={1} sx={{ mt: 2 }}>
        {candidates.map((game) => {
          const choice = selected.get(game.id);
          const locked = choice?.locked ?? false;
          return (
            <Box
              key={game.id}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) auto",
                alignItems: "center",
                gap: 1,
                p: 1.25,
                border: "1px solid",
                borderColor: choice ? "primary.main" : "divider",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: ".78rem", fontWeight: 800 }}>{game.opponent}</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: ".66rem" }}>
                  {formatDate(game.playedAt)} · {game.exactTimeControl ? formatClock(game.exactTimeControl) : ""}
                </Typography>
              </Box>
              {locked ? (
                <Button component={RouterLink} to={`/games/${game.id}`} size="small">
                  Analyzed · locked
                </Button>
              ) : (
                <Button
                  size="small"
                  disabled={savingId !== null}
                  color={choice ? "secondary" : "primary"}
                  onClick={() => void choose(game.id)}
                >
                  {savingId === game.id ? "Saving…" : choice ? "Remove" : "Choose"}
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
